import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ResetPassword({ token, onBackToLogin }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-reset-token?token=${token}`);
      const data = await response.json();
      
      if (response.ok) {
        setTokenValid(true);
        setUserEmail(data.email);
      } else {
        setError(data.detail || 'Invalid or expired reset link');
        setTokenValid(false);
      }
    } catch (err) {
      setError('Unable to verify reset link. Please try again.');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-page">
      <style>{`
        .reset-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%);
          padding: 1rem;
        }
        .reset-card {
          background: white;
          padding: 2.5rem;
          border-radius: 8px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          width: 100%;
          max-width: 450px;
        }
        .reset-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .reset-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }
        .reset-icon svg {
          color: white;
        }
        .reset-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 0.5rem;
        }
        .reset-subtitle {
          color: #64748B;
          font-size: 0.9rem;
        }
        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .password-input-wrapper input {
          padding-right: 45px;
        }
        .password-toggle-btn {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748B;
          transition: color 0.2s;
        }
        .password-toggle-btn:hover {
          color: #0F172A;
        }
        .password-requirements {
          font-size: 0.8rem;
          color: #64748B;
          margin-top: 6px;
        }
        .success-container {
          text-align: center;
          padding: 2rem 0;
        }
        .success-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .success-icon svg {
          color: white;
          width: 40px;
          height: 40px;
        }
        .success-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 0.5rem;
        }
        .success-message {
          color: #64748B;
          margin-bottom: 1.5rem;
        }
        .error-container {
          text-align: center;
          padding: 2rem 0;
        }
        .error-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .error-icon svg {
          color: white;
          width: 40px;
          height: 40px;
        }
        .error-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 0.5rem;
        }
        .error-message {
          color: #64748B;
          margin-bottom: 1.5rem;
        }
        .loading-spinner {
          text-align: center;
          padding: 3rem 0;
        }
        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #E2E8F0;
          border-top-color: #3B82F6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748B;
          font-size: 0.875rem;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          margin-bottom: 1.5rem;
        }
        .back-btn:hover {
          color: #0F172A;
        }
        .email-badge {
          display: inline-block;
          background: #E0F2FE;
          color: #0369A1;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          margin-top: 8px;
        }
      `}</style>

      <div className="reset-card">
        {verifying ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p style={{ color: '#64748B' }}>Verifying reset link...</p>
          </div>
        ) : success ? (
          <div className="success-container">
            <div className="success-icon">
              <CheckCircle />
            </div>
            <h2 className="success-title" data-testid="success-title">Password Reset!</h2>
            <p className="success-message">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <button 
              className="btn btn-primary" 
              onClick={onBackToLogin}
              data-testid="back-to-login-btn"
              style={{ width: '100%' }}
            >
              Back to Login
            </button>
          </div>
        ) : !tokenValid ? (
          <div className="error-container">
            <div className="error-icon">
              <AlertCircle />
            </div>
            <h2 className="error-title" data-testid="error-title">Link Expired</h2>
            <p className="error-message">
              {error || 'This password reset link is invalid or has expired. Please request a new one.'}
            </p>
            <button 
              className="btn btn-primary" 
              onClick={onBackToLogin}
              data-testid="request-new-link-btn"
              style={{ width: '100%' }}
            >
              Request New Link
            </button>
          </div>
        ) : (
          <>
            <button 
              type="button" 
              className="back-btn" 
              onClick={onBackToLogin}
              data-testid="back-btn"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
            
            <div className="reset-header">
              <div className="reset-icon">
                <Lock size={28} />
              </div>
              <h2 className="reset-title" data-testid="reset-title">Set New Password</h2>
              <p className="reset-subtitle">
                Create a new password for your account
              </p>
              <span className="email-badge">{userEmail}</span>
            </div>
            
            {error && (
              <div className="alert alert-error" data-testid="reset-error">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} data-testid="reset-password-form">
              <div className="form-group">
                <label className="form-label">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-input"
                    data-testid="new-password-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-new-password-btn"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="password-requirements">Must be at least 8 characters</p>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-input"
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    data-testid="toggle-confirm-password-btn"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary" 
                data-testid="reset-submit-btn"
                style={{ width: '100%' }} 
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
